import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DropDownSelectorValues,
  ProjectStatus as ProjectStatusEnum,
  SubPage,
  Tab,
} from '@symfa-inc/providence-verizon-types';
import { Spin } from 'antd';
import { HttpService } from '@core/services';
import { LeasingHttpService } from '@core/services/http';
import { useLeasingTabPrefetch } from '@core/utils/hooks';
import {
  canUserEditByProjectStatusAndRoles,
  getProjectBundleIDParamFromUrl,
} from '@core/utils/methods';
import { JurisdictionAudit } from '@modules/client/pages/permitting/browse';
import { NoJurisdiction } from '@modules/client/pages/permitting/browse/details/tabs/jurisdiction-audit/components';
import { EditablePage } from '@shared/components';
import { LeasingActions } from '@store/actions';
import {
  DDVSelectors,
  LeasingSelectors,
  PermittingSelectors,
  UserSelectors,
} from '@store/selectors';

export const LeasingJurisdictionAudit: FC = () => {
  const dispatch = useDispatch();
  const [projectBundleID] = getProjectBundleIDParamFromUrl();
  const permissions = useSelector(UserSelectors.getPermittingBrowsePermissions);

  const isLoading = useSelector(
    LeasingSelectors.getTabLoadingState('jurisdictionAudit'),
  );
  const isDataLoaded = useSelector(
    LeasingSelectors.getTabLoadedState('jurisdictionAudit'),
  );

  const jurisdiction = useSelector(
    PermittingSelectors.getJurisdictionAuditTabData,
  );

  const projectType = useSelector(LeasingSelectors.getProjectType);
  const leasingProjectId = useSelector(LeasingSelectors.getProjectId);
  const projectTypePermission = useSelector(
    UserSelectors.canEditProjectByProjectType(projectType),
  );
  const canEdit = useSelector(
    UserSelectors.canEdit(
      'permitting',
      SubPage.PermittingBrowse,
      Tab.JurisdictionAudit,
    ),
  );

  const userRoles = useSelector(UserSelectors.getUserRoles);
  const projectStatusId = useSelector(LeasingSelectors.getProjectStatus);
  const projectStatus = useSelector(
    DDVSelectors.getCurrentDDVValueById<ProjectStatusEnum>(
      DropDownSelectorValues.ProjectStatus,
      projectStatusId,
    ),
  );

  useLeasingTabPrefetch('jurisdictionAudit', projectBundleID);

  const fetchData = async () => {
    if (!isDataLoaded) {
      dispatch(
        LeasingActions.setTabLoading.done({
          tabName: 'jurisdictionAudit',
          isLoading: true,
        }),
      );

      await HttpService.getHttpRequests(
        LeasingHttpService,
      ).getLeasingJurisdictionAuditData(projectBundleID);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dispatch, isDataLoaded]);

  if (isLoading || !isDataLoaded) {
    return (
      <div className="prov-permitting-jurisdiction-audit loading">
        <Spin size="large" />
      </div>
    );
  }

  // todo: to shared import JurisdictionAudit : NoJurisdiction
  return (
    <EditablePage
      editableComponent={jurisdiction.id ? JurisdictionAudit : NoJurisdiction}
      permissions={permissions}
      canUserEdit={
        !!jurisdiction.id &&
        canEdit &&
        projectTypePermission &&
        canUserEditByProjectStatusAndRoles(projectStatus, userRoles)
      }
      data={{ ...jurisdiction, leasingProjectId }}
    />
  );
};
